<template>
   <div v-if="!isLoading">
      <!-- 📱 GORGEOUS LIST VIEW FOR WEB CUSTOMERS -->
      <div v-if="isWeb" class="flex flex-col gap-6">
         <!-- Premium Stacked List -->
         <div v-if="bookings && bookings.length > 0" class="flex flex-col gap-4">
            <div v-for="booking in bookings" :key="booking.number" class="bg-white rounded-2xl border border-gray-100 p-5 hover:border-theme-active/30 hover:shadow-sm transition-all duration-300 flex flex-col md:flex-row md:items-center justify-between gap-4">
               <!-- 1. Left Column: Details (Booking Info) -->
               <div class="flex-grow flex flex-col justify-center">
                  <!-- Row 1: Booking ID & Pickup Schedule & Status Badge -->
                  <div class="flex flex-wrap items-center gap-x-4 gap-y-2">
                     <div class="flex flex-col">
                        <span class="font-extrabold text-gray-900 text-lg">#{{ booking.number }}</span>
                        <span v-if="booking.refNumber && !booking.refNumber.startsWith('ORD-')" class="text-[10px] text-indigo-600 font-bold mt-0.5">Online Ref: #{{ booking.refNumber }}</span>
                     </div>
                     <div class="flex items-center gap-1.5 text-gray-500 text-sm font-semibold">
                        <i class="pi pi-calendar text-theme-active" style="font-size: 0.9rem"></i>
                        <span>{{ booking.search?.pickUp?.date }} at {{ booking.search?.pickUp?.time }}</span>
                     </div>
                      <span :class="getBookingStatusClass(booking)" class="inline-flex items-center justify-center px-3 py-1 rounded-full text-[9px] font-extrabold uppercase tracking-widest shadow-sm">
                         {{ getBookingStatusText(booking) }}
                      </span>
                  </div>

                  <!-- Row 2: Vehicle Model & Location -->
                  <div class="flex flex-col sm:flex-row sm:items-center gap-x-6 gap-y-2 mt-3 text-xs text-gray-500 border-t border-gray-50/50 pt-2.5">
                     <div class="flex items-center gap-2">
                        <i class="pi pi-car text-gray-400" style="font-size: 0.9rem"></i>
                        <span class="font-bold text-gray-700 text-sm">{{ booking.car?.name || booking.car_meta?.name || 'Standard Vehicle' }}</span>
                     </div>
                     <div v-if="booking.search?.pickUp?.location?.name" class="flex flex-wrap items-center gap-2 flex-1 text-xs md:text-sm">
                        <i class="pi pi-map-marker text-theme-active font-bold" style="font-size: 0.9rem"></i>
                        <span class="font-bold text-gray-800 line-clamp-1 max-w-[280px]">{{ booking.search?.pickUp?.location?.name }}</span>
                        
                        <template v-if="(booking.type === 'distance' || booking.search?.type === 'distance') && booking.search?.dropOff?.location?.name">
                           <i class="pi pi-arrow-right text-gray-400 mx-1" style="font-size: 0.75rem"></i>
                           <i class="pi pi-map-marker text-red-500 font-bold" style="font-size: 0.9rem"></i>
                           <span class="font-bold text-gray-800 line-clamp-1 max-w-[280px]">{{ booking.search?.dropOff?.location?.name }}</span>
                        </template>
                        <template v-else-if="booking.type === 'hourly' || booking.search?.type === 'hourly' || booking.type === 'chauffer'">
                           <span class="ml-2 px-2 py-0.5 rounded bg-gray-100 text-gray-600 font-bold text-[10px] uppercase">
                              Hourly ({{ booking.search?.hours || 10 }} hrs)
                           </span>
                        </template>
                     </div>
                  </div>
               </div>

               <!-- 2. Right Column: Action Buttons -->
               <div v-if="booking.status !== 'cancelled'" class="flex flex-wrap items-center gap-2 flex-shrink-0 border-t md:border-t-0 pt-3 md:pt-0 border-gray-50">
                  <button @click="downloadDocument('invoice', booking)" type="button" class="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 text-xs font-bold rounded-xl bg-indigo-50 text-indigo-600 hover:bg-indigo-600 hover:text-white transition-all duration-300 border border-indigo-200/50 min-w-[110px] shadow-sm">
                     <i class="pi pi-file" style="font-size: 0.75rem"></i> Invoice
                  </button>
                  <button v-if="booking.status === 'confirmed' && booking.payment?.status === 'paid'" @click="downloadDocument('voucher', booking)" type="button" class="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 text-xs font-bold rounded-xl bg-theme-active/10 text-theme-active hover:bg-theme-active hover:text-white transition-all duration-300 border border-theme-active/20 min-w-[110px] shadow-sm">
                     <i class="pi pi-download" style="font-size: 0.75rem"></i> Voucher
                  </button>
                  <template v-if="booking.source === 'Online' || !booking.source">
                     <button @click="handleEditBooking(booking)" type="button" class="inline-flex items-center justify-center gap-1 px-4 py-2.5 text-xs font-bold rounded-xl bg-amber-50 text-amber-600 hover:bg-amber-600 hover:text-white transition-all duration-300 border border-amber-200/50 min-w-[110px] shadow-sm">
                        <i class="pi pi-pencil" style="font-size: 0.75rem"></i> Edit
                     </button>
                     <button @click="handleCancelBooking(booking)" type="button" class="inline-flex items-center justify-center gap-1 px-4 py-2.5 text-xs font-bold rounded-xl bg-rose-50 text-rose-600 hover:bg-rose-600 hover:text-white transition-all duration-300 border border-rose-200/50 min-w-[110px] shadow-sm">
                        <i class="pi pi-times-circle" style="font-size: 0.75rem"></i> Cancel
                     </button>
                  </template>
               </div>
            </div>
         </div>
         <!-- Empty State -->
         <div v-else class="text-center py-16 bg-gray-50/50 rounded-2xl border border-dashed border-gray-200 p-8">
            <div class="h-14 w-14 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4 text-gray-400">
               <i class="pi pi-calendar-times" style="font-size: 1.5rem"></i>
            </div>
            <h3 class="text-gray-900 font-bold text-base mb-1">No Reservations Found</h3>
            <p class="text-sm font-semibold text-gray-500 max-w-sm mx-auto leading-relaxed font-regular">You haven't made any transfer bookings yet. Ready to plan your next transfer journey?</p>
         </div>
      </div>

      <!-- 🖥️ STANDARD TABLE VIEW FOR AGENTS / CORPORATE / ADMINS (UNTOUCHED) -->
      <DataTable v-else
         exportFilename="bookings"
         ref="dt"
         class="text-sm"
         v-model:filters="filters" 
         :value="filteredBookings"
         paginator showGridlines :rows="10" 
         dataKey="id"
         filterDisplay="menu" 
         :loading="isLoading"
         :globalFilterFields="[
             'number',
             'status', 
             'search.pickUp.date', 
             'search.pickUp.time', 
             'search.pickUp.location.name',
             'car.name',
             'contact.name', 
             'contact.email', 
             'contact.phone',
             'bookedBy.name',
             'source',
             'createdAt',
          ]"
      >
         <template #header>
            <div class="flex flex-col gap-4 bg-gray-50/50 p-4 rounded-2xl mb-4 border border-gray-100/85 shadow-sm">
               <!-- Row 1: Title and Main Actions -->
               <div class="flex flex-col sm:flex-row gap-3 w-full justify-between items-center">
                  <div class="text-gray-900 font-bold text-base flex items-center gap-2">
                     <i class="pi pi-list text-theme-active" style="font-size: 1rem; font-weight: bold;"></i>
                     Reservation Records
                  </div>
                  <div class="flex gap-2 justify-end w-full sm:w-auto">
                     <button type="button" @click="clearFilter()" class="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-xl border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 transition-all duration-300 shadow-sm">
                        <i class="pi pi-filter-slash" style="font-size: 0.8rem"></i> Clear
                     </button>
                     <button type="button" @click="exportCSV($event)" class="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-xl bg-theme-active text-white hover:bg-theme-active/90 transition-all duration-300 shadow-sm">
                        <i class="pi pi-external-link" style="font-size: 0.8rem"></i> Export
                     </button>
                  </div>
               </div>
               
               <!-- Row 2: Search & Dropdown Filters -->
               <div class="flex flex-wrap gap-3 items-center w-full">
                  <!-- Global Search -->
                  <div class="relative flex-grow sm:flex-grow-0">
                     <span class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                        <i class="pi pi-search" style="font-size: 0.8rem"></i>
                     </span>
                     <input v-model="filters['global'].value" placeholder="Search reservations..." class="pl-9 pr-4 py-2 border border-gray-200 rounded-xl text-sm w-full sm:w-64 bg-white focus:outline-none focus:ring-2 focus:ring-theme-active focus:border-transparent transition-all duration-300" />
                  </div>

                  <!-- Date Filter Dropdown -->
                  <div class="relative w-full sm:w-48">
                     <select v-model="selectedDateScope" class="w-full pl-3 pr-8 py-2 border border-gray-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-theme-active focus:border-transparent appearance-none transition-all duration-300 cursor-pointer">
                        <option v-for="scope in scopes" :key="scope.id" :value="scope.id">{{ scope.name }}</option>
                     </select>
                     <span class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none text-gray-400">
                        <i class="pi pi-chevron-down text-xs"></i>
                     </span>
                  </div>

                  <!-- Custom Date Range inputs -->
                  <div v-if="selectedDateScope === 'custom'" class="flex items-center gap-2 w-full sm:w-auto">
                     <input type="date" v-model="startDate" class="border border-gray-250 rounded-xl px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-theme-active focus:border-transparent">
                     <span class="text-gray-400 text-sm">to</span>
                     <input type="date" v-model="endDate" class="border border-gray-250 rounded-xl px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-theme-active focus:border-transparent">
                  </div>

                  <!-- Status Filter Dropdown -->
                  <div class="relative w-full sm:w-44">
                     <select v-model="statusFilterValue" @change="applyStatusFilter" class="w-full pl-3 pr-8 py-2 border border-gray-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-theme-active focus:border-transparent appearance-none transition-all duration-300">
                        <option value="">All Statuses</option>
                        <option v-for="status in STATUSES" :key="status.id" :value="status.name">{{ status.title }}</option>
                     </select>
                     <span class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none text-gray-400">
                        <i class="pi pi-chevron-down text-xs"></i>
                     </span>
                  </div>

                  <!-- Booked By Filter Dropdown -->
                  <div v-if="!isWeb && mySubAgents && mySubAgents.length > 0" class="relative w-full sm:w-48">
                     <select v-model="bookedByFilterValue" @change="applyBookedByFilter" class="w-full pl-3 pr-8 py-2 border border-gray-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-theme-active focus:border-transparent appearance-none transition-all duration-300">
                        <option value="">All Agents</option>
                        <option v-for="agent in mySubAgents" :key="agent.id" :value="agent.name">{{ agent.name }}</option>
                     </select>
                     <span class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none text-gray-400">
                        <i class="pi pi-chevron-down text-xs"></i>
                     </span>
                  </div>

                  <!-- Source Filter Dropdown -->
                  <div v-if="!isWeb" class="relative w-full sm:w-44">
                     <select v-model="sourceFilterValue" @change="applySourceFilter" class="w-full pl-3 pr-8 py-2 border border-gray-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-theme-active focus:border-transparent appearance-none transition-all duration-300">
                        <option value="">All Sources</option>
                        <option value="Online">Online</option>
                        <option value="Offline">Offline</option>
                     </select>
                     <span class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none text-gray-400">
                        <i class="pi pi-chevron-down text-xs"></i>
                     </span>
                  </div>
               </div>
            </div>
         </template>
         <template #empty> No bookings found. </template>
         <template #loading> Loading bookings data. Please wait. </template>
         <Column field="number" header="Booking Number & Date" exportHeader="Booking Number">
            <template #body="{ data }">
               <div class="flex flex-col">
                  <span class="font-bold text-gray-900 text-sm">#{{ data.number }}</span>
                  <span v-if="data.refNumber && !data.refNumber.startsWith('ORD-')" class="text-[10px] text-indigo-600 font-bold mt-0.5">Online Ref: #{{ data.refNumber }}</span>
                  <span class="text-xs text-gray-500 font-medium mt-1">{{ data.createdAt ? formattedDateTime(data.createdAt) : 'N/A' }}</span>
               </div>
            </template>
            <template #filter="{ filterModel }">
               <InputText v-model="filterModel.value" type="text" class="p-column-filter" placeholder="Search booking number" />
            </template>
         </Column>
         <Column field="contact.name" exportHeader="Customer Name" class="hidden">
            <template #body="{ data }">
               {{ data.contact.name }}
            </template>
         </Column>
         <Column field="contact.phone" exportHeader="Customer Phone" class="hidden">
            <template #body="{ data }">
               {{ data.contact.phone }}
            </template>
         </Column>
         <Column header="Booking Details" :exportable="false">
            <template #body="{ data }">
               <div class="flex flex-col gap-1 text-xs text-gray-700">
                  <div class="flex items-center gap-1.5">
                     <span class="font-bold text-gray-900">Booked By:</span>
                     <span class="font-semibold text-gray-600">{{ data.bookedBy ? data.bookedBy.name : 'N/A' }}</span>
                  </div>
                  <div class="flex items-center gap-1.5">
                     <span class="font-bold text-gray-900">Pickup Date:</span>
                     <span class="font-medium text-gray-600">{{ data.search?.pickUp?.date }} - {{ data.search?.pickUp?.time }}</span>
                  </div>
                  <div class="flex items-center gap-1.5">
                     <span class="font-bold text-gray-900">Booked Car:</span>
                     <span class="font-semibold text-theme-active">{{ data.car?.name || data.car_meta?.name || 'Standard Vehicle' }}</span>
                  </div>
                  <div v-if="data.loyalty_points" class="flex items-center gap-1.5 mt-1">
                     <span class="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 font-extrabold text-[10px] uppercase border border-emerald-200">
                        +{{ Number(data.loyalty_points).toFixed(2) }} Loyalty Points
                     </span>
                  </div>
                  <div class="flex flex-col gap-0.5 mt-1 border-t border-gray-100 pt-1.5">
                     <div class="flex items-start gap-1">
                        <i class="pi pi-map-marker text-green-600 mt-0.5" style="font-size: 0.7rem"></i>
                        <span class="font-medium text-gray-600 line-clamp-1" :title="data.search?.pickUp?.location?.name">
                           <strong>Pick:</strong> {{ data.search?.pickUp?.location?.name || 'N/A' }}
                        </span>
                     </div>
                     <div v-if="(data.type === 'distance' || data.search?.type === 'distance') && data.search?.dropOff?.location?.name" class="flex items-start gap-1">
                        <i class="pi pi-map-marker text-red-500 mt-0.5" style="font-size: 0.7rem"></i>
                        <span class="font-medium text-gray-600 line-clamp-1" :title="data.search?.dropOff?.location?.name">
                           <strong>Drop:</strong> {{ data.search?.dropOff?.location?.name }}
                        </span>
                     </div>
                      <div v-else-if="data.type === 'hourly' || data.search?.type === 'hourly' || data.type === 'chauffer'" class="flex items-center gap-1">
                         <span class="px-2 py-0.5 rounded bg-gray-100 text-gray-600 font-bold text-[9px] uppercase">
                            Hourly ({{ data.search?.hours || 10 }} hrs)
                         </span>
                      </div>
                  </div>
               </div>
            </template>
         </Column>
         <Column field="search.pickUp.location.name" exportHeader="Pick up" class="hidden">
            <template #body="{ data }">
               {{ data.search.pickUp.location.name }}
            </template>
         </Column>
         <Column field="search.pickUp.date" exportHeader="Pick up date" class="hidden">
            <template #body="{ data }">
               {{ data.search.pickUp.date }}
            </template>
         </Column>
         <Column field="search.pickUp.time" exportHeader="Pick up time" class="hidden">
            <template #body="{ data }">
               {{ data.search.pickUp.time }}
            </template>
         </Column>
         <Column header="Amount" field="totalAmount" style="min-width: 8rem;">
            <template #body="{ data }">
               <div class="flex flex-col gap-1.5 justify-center">
                  <span class="font-bold text-gray-900 text-sm">
                     USD {{ parseFloat(data.totalAmount || 0).toFixed(2) }}
                  </span>
                  <span :class="data.source === 'Online' ? 'bg-teal-50 text-teal-700 border border-teal-200' : 'bg-gray-50 text-gray-700 border border-gray-200'" class="inline-flex items-center justify-center px-2.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider w-fit shadow-xs">
                     {{ data.source || 'Online' }}
                  </span>
               </div>
            </template>
         </Column>
         <Column header="Payment & Booking Status" field="status">
            <template #body="{ data }">
               <div class="flex flex-col items-center gap-1">
                  <span :class="getBookingStatusClass(data)" class="inline-flex items-center justify-center px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider min-w-[120px] shadow-sm text-center">
                     {{ getBookingStatusText(data) }}
                  </span>
                  <span v-if="data.payment?.status === 'paid' && data.payment?.date" class="text-[9px] text-gray-500 font-medium mt-0.5">
                     {{ formattedDateTime(data.payment.date) }}
                  </span>
               </div>
            </template>
         </Column>
         <Column header="Actions" :exportable="false" style="min-width: 16rem;">
            <template #body="{ data }">
               <div v-if="data.status !== 'cancelled'" class="flex flex-wrap items-center gap-1.5">
                  <button v-if="data.status === 'confirmed' && data.payment?.status === 'paid'" @click="downloadDocument('voucher', data)" type="button" class="inline-flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-bold rounded-lg bg-theme-active/10 text-theme-active hover:bg-theme-active hover:text-white transition-all duration-300 shadow-sm border border-theme-active/20">
                     <i class="pi pi-download" style="font-size: 0.65rem"></i> Voucher
                  </button>
                  <button @click="downloadDocument('invoice', data)" type="button" class="inline-flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-bold rounded-lg bg-indigo-50 text-indigo-600 hover:bg-indigo-600 hover:text-white transition-all duration-300 shadow-sm border border-indigo-200/50">
                     <i class="pi pi-file" style="font-size: 0.65rem"></i> Invoice
                  </button>
                  
                  <template v-if="data.source === 'Online' || !data.source">
                     <button @click="handleEditBooking(data)" type="button" class="inline-flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-bold rounded-lg bg-amber-50 text-amber-600 hover:bg-amber-600 hover:text-white transition-all duration-300 shadow-sm border border-amber-200/50">
                        <i class="pi pi-pencil" style="font-size: 0.65rem"></i> Edit
                     </button>
                     <button @click="handleCancelBooking(data)" type="button" class="inline-flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-bold rounded-lg bg-rose-50 text-rose-600 hover:bg-rose-600 hover:text-white transition-all duration-300 shadow-sm border border-rose-200/50">
                        <i class="pi pi-times-circle" style="font-size: 0.65rem"></i> Cancel
                     </button>
                  </template>
               </div>
            </template>
         </Column>
      </DataTable>
   </div>

   <!-- HIDDEN DOWNLOAD AREA -->
   <div v-if="downloadingItem" class="fixed top-[-9999px] left-[-9999px]" style="pointer-events: none; width: 800px;">
      <div v-if="downloadingItem.type === 'invoice'" ref="downloadInvoiceRef" class="w-full bg-white">
         <Invoice :data="downloadingItem.data" />
      </div>
      <div v-if="downloadingItem.type === 'voucher'" ref="downloadVoucherRef" class="w-full bg-white">
         <Voucher :data="downloadingItem.data" />
      </div>
   </div>

   <!-- Edit Booking Modal -->
   <div v-if="isEditModalOpen" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div class="bg-white rounded-2xl max-w-2xl w-full p-6 shadow-2xl flex flex-col max-h-[90vh] overflow-hidden">
         <div class="flex justify-between items-center pb-4 border-b">
            <h3 class="text-lg font-bold text-gray-900">
               {{ editStep === 'form' ? 'Edit Booking Details' : 'Confirm Changes' }}
            </h3>
            <button @click="isEditModalOpen = false" class="text-gray-400 hover:text-gray-600">
               <i class="pi pi-times text-lg"></i>
            </button>
         </div>

         <!-- Step 1: Form editing -->
         <div v-if="editStep === 'form'" class="flex-grow overflow-y-auto py-4 space-y-4">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
               <div>
                  <label class="block text-xs font-semibold text-gray-600 mb-1">Contact Name</label>
                  <input type="text" v-model="editForm.contact_name" class="w-full px-3 py-2 border rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-theme-active" />
               </div>
               <div>
                  <label class="block text-xs font-semibold text-gray-600 mb-1">Contact Email</label>
                  <input type="email" v-model="editForm.contact_email" class="w-full px-3 py-2 border rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-theme-active" />
               </div>
               <div>
                  <label class="block text-xs font-semibold text-gray-600 mb-1">Contact Phone</label>
                  <input type="text" v-model="editForm.contact_phone" class="w-full px-3 py-2 border rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-theme-active" />
               </div>
               <div>
                  <label class="block text-xs font-semibold text-gray-600 mb-1">Flight Number</label>
                  <input type="text" v-model="editForm.contact_flight_number" class="w-full px-3 py-2 border rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-theme-active" placeholder="e.g. EK123" />
               </div>
               <div>
                  <label class="block text-xs font-semibold text-gray-600 mb-1">Pickup Date</label>
                  <input type="date" v-model="editForm.pickup_date" class="w-full px-3 py-2 border rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-theme-active" />
               </div>
               <div>
                  <label class="block text-xs font-semibold text-gray-600 mb-1">Pickup Time</label>
                  <input type="time" v-model="editForm.pickup_time" class="w-full px-3 py-2 border rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-theme-active" />
               </div>
               <div v-if="editingBooking?.type === 'hourly' || editingBooking?.search?.type === 'hourly' || editingBooking?.type === 'chauffer'">
                  <label class="block text-xs font-semibold text-gray-600 mb-1">Duration (Hours)</label>
                  <input type="number" v-model="editForm.hours" class="w-full px-3 py-2 border rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-theme-active" min="1" />
               </div>
            </div>

            <div class="relative">
                <label class="block text-xs font-semibold text-gray-600 mb-1">Pickup Location</label>
                <div class="relative flex items-center">
                   <input type="text" v-model="editForm.pickup_location" @input="handlePickupSearch" class="w-full px-3 py-2 border rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-theme-active pr-8" placeholder="Type to search pickup location..." />
                   <div v-if="isPickupLoading" class="absolute right-2.5">
                      <i class="pi pi-spin pi-spinner text-gray-400 text-sm"></i>
                   </div>
                </div>
                <ul v-if="isPickupDropOpen && pickupAddresses.length" class="z-50 !mt-1 max-h-[200px] overflow-y-auto absolute top-full left-0 w-full shadow-lg bg-white border border-gray-200 rounded-lg">
                   <li @click.stop="selectPickupAddress(item)" v-for="(item, index) in pickupAddresses" :key="index" class="flex items-center gap-2 py-2 px-3 hover:bg-gray-50 border-b border-gray-100 text-gray-700 text-xs cursor-pointer">
                      <i :class="item.isAirport ? 'pi pi-plane' : 'pi pi-map-marker'" class="text-gray-400"></i>
                      <span>{{ item.name }}</span>
                   </li>
                </ul>
             </div>

             <div v-if="!(editingBooking?.type === 'hourly' || editingBooking?.search?.type === 'hourly' || editingBooking?.type === 'chauffer')" class="relative">
                <label class="block text-xs font-semibold text-gray-600 mb-1">Drop-off Location</label>
                <div class="relative flex items-center">
                   <input type="text" v-model="editForm.dropoff_location" @input="handleDropoffSearch" class="w-full px-3 py-2 border rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-theme-active pr-8" placeholder="Type to search drop-off location..." />
                   <div v-if="isDropoffLoading" class="absolute right-2.5">
                      <i class="pi pi-spin pi-spinner text-gray-400 text-sm"></i>
                   </div>
                </div>
                <ul v-if="isDropoffDropOpen && dropoffAddresses.length" class="z-50 !mt-1 max-h-[200px] overflow-y-auto absolute top-full left-0 w-full shadow-lg bg-white border border-gray-200 rounded-lg">
                   <li @click.stop="selectDropoffAddress(item)" v-for="(item, index) in dropoffAddresses" :key="index" class="flex items-center gap-2 py-2 px-3 hover:bg-gray-50 border-b border-gray-100 text-gray-700 text-xs cursor-pointer">
                      <i :class="item.isAirport ? 'pi pi-plane' : 'pi pi-map-marker'" class="text-gray-400"></i>
                      <span>{{ item.name }}</span>
                   </li>
                </ul>
             </div>

            <div>
               <label class="block text-xs font-semibold text-gray-600 mb-1">Special Comments</label>
               <textarea v-model="editForm.comments" rows="3" class="w-full px-3 py-2 border rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-theme-active" placeholder="Any additional request..."></textarea>
            </div>
         </div>

         <!-- Step 2: Preview changed fields -->
         <div v-else class="flex-grow overflow-y-auto py-4 space-y-4">
            <p class="text-sm font-semibold text-gray-600">Please review the changes you are requesting for Booking <span class="font-bold text-gray-900">#{{ editingBooking?.number }}</span>:</p>
            <div class="border rounded-xl divide-y bg-gray-50 overflow-hidden">
               <div v-for="item in changeSummary" :key="item.key" class="p-3 grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm">
                  <span class="font-bold text-gray-700">{{ item.field }}</span>
                  <span class="text-red-500 line-through truncate">{{ item.old || '(Empty)' }}</span>
                  <span class="text-green-600 font-semibold truncate">&rarr; {{ item.new || '(Empty)' }}</span>
               </div>
            </div>
         </div>

         <!-- Actions -->
         <div class="pt-4 border-t flex justify-end gap-2">
            <button v-if="editStep === 'form'" @click="showChangePreview" type="button" class="px-5 py-2 text-sm font-bold bg-theme-active text-white rounded-xl hover:bg-theme-active/90 shadow-sm">
               Save
            </button>
            <button v-if="editStep === 'preview'" @click="editStep = 'form'" type="button" class="px-5 py-2 text-sm font-semibold border rounded-xl hover:bg-gray-50 text-gray-700 bg-white">
               Back
            </button>
            <button v-if="editStep === 'preview'" @click="submitChangeRequest" type="button" class="px-5 py-2 text-sm font-bold bg-green-600 text-white rounded-xl hover:bg-green-700 shadow-sm">
               Confirm Request
            </button>
            <button @click="isEditModalOpen = false" type="button" class="px-5 py-2 text-sm font-semibold border rounded-xl hover:bg-gray-50 text-gray-700 bg-white">
               Cancel
            </button>
         </div>
      </div>
   </div>

   <!-- Cancellation Request Modal -->
   <div v-if="isCancelModalOpen" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div class="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl space-y-4">
         <div class="flex justify-between items-center pb-2 border-b">
            <h3 class="text-lg font-bold text-gray-900">Cancel Booking Request</h3>
            <button @click="isCancelModalOpen = false" class="text-gray-400 hover:text-gray-600">
               <i class="pi pi-times text-lg"></i>
            </button>
         </div>

         <div class="space-y-3">
            <p class="text-sm font-medium text-gray-700">
               Are you sure you want to request cancellation for booking <span class="font-bold text-gray-950">#{{ editingBooking?.number }}</span>?
            </p>
            <div>
               <label class="block text-xs font-bold text-gray-600 mb-1">Reason for Cancellation <span class="text-red-500">*</span></label>
               <textarea v-model="cancelReason" rows="3" :class="cancelReasonError ? 'border-red-500 focus:ring-red-500' : 'border-gray-200 focus:ring-theme-active'" class="w-full px-3 py-2 border rounded-lg text-sm bg-white outline-none focus:ring-2" placeholder="Please specify the reason..."></textarea>
               <p v-if="cancelReasonError" class="text-red-500 text-xs font-semibold mt-1">Cancellation reason is mandatory.</p>
            </div>
         </div>

         <div class="pt-2 border-t flex justify-end gap-2">
            <button @click="submitCancellationRequest" type="button" class="px-5 py-2 text-sm font-bold bg-red-600 text-white rounded-xl hover:bg-red-700 shadow-sm">
               Submit Request
            </button>
            <button @click="isCancelModalOpen = false" type="button" class="px-5 py-2 text-sm font-semibold border rounded-xl hover:bg-gray-50 text-gray-700 bg-white">
               Cancel
            </button>
         </div>
      </div>
   </div>

   <Notification :show="notif.show" :type="notif.type" :message="notif.message" :messageDetails="notif.messageDetails" @closed="notifClosed"/>

   <div v-if="isLoading" class="text-center font-bold h-[50px] mt-12 w-full text-gray-400">
      Loading data please wait...
   </div>
   <!--
      <SlideOver :show="openDialog" @closed="openDialog = false">
         <template v-slot:content>
            <BookingDetails :data="selectedBooking" />
         </template>
      </SlideOver>
   -->
</template>

<script setup lang="ts">
import { ref, onMounted, computed, onBeforeUnmount } from 'vue'
import { useRoute } from 'vue-router'
import Agent from '@/composables/admin/agent'
import Corporate from '@/composables/admin/corporate'
import axios from 'axios'
import { handleApiError, isLoggedIn, isAdminLoggedIn, saveBookingNumber, isMakePaymentVisible, formattedDateTime, logout, getBackendBaseUrl, getUserToken, sortPlacePredictions } from '@/utils/common'
import Location from '@/composables/Location'
import SlideOver from '@/widgets/SlideOver.vue'
import BookingDetails from '@/components/admin/BookingDetails.vue'
import Invoice from '@/components/booking/Invoice.vue'
import Voucher from '@/components/booking/Voucher.vue'
import Notification from '@/widgets/Notification.vue'
import { NotificationType } from '@/types/index'
// @ts-ignore
import html2pdf from 'html2pdf.js'
import { STATUSES } from '@/utils/constants'
import { FilterMatchMode, FilterOperator } from 'primevue/api'
import { saveUserSearch } from '@/services/search-service'
import CarService from '@/services/car-service'
import Admin from '@/composables/admin'
import router from '@/router'

const { saveSelectedCar } = CarService()
const route = useRoute()
const bookings = ref<any>(null)
const selectedDateScope = ref<string>('this_month')
const startDate = ref<string>('')
const endDate = ref<string>('')
const scopes = ref([
   { id: 'all_time', name: 'Lifetime' },
   { id: 'today', name: 'Today' },
   { id: 'this_week', name: 'This Week' },
   { id: 'this_month', name: 'This Month' },
   { id: 'last_month', name: 'Last Month' },
   { id: 'this_year', name: 'This Year' },
   { id: 'last_year', name: 'Last Year' },
   { id: 'custom', name: 'Custom Date Range' }
])

const filteredBookings = computed(() => {
   if (!bookings.value) return []
   const scope = selectedDateScope.value
   if (scope === 'all_time') return bookings.value

   const now = new Date()
   let start: Date | null = null
   let end: Date | null = null

   if (scope === 'today') {
      start = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0)
      end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59)
   } else if (scope === 'this_week') {
      const currentDay = now.getDay()
      const distance = now.getDate() - currentDay + (currentDay === 0 ? -6 : 1) // Start of week (Monday)
      start = new Date(now.setDate(distance))
      start.setHours(0,0,0,0)
      
      const lastday = new Date(start)
      lastday.setDate(start.getDate() + 6)
      end = new Date(lastday)
      end.setHours(23,59,59,999)
   } else if (scope === 'this_month') {
      start = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0)
      end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59)
   } else if (scope === 'last_month') {
      start = new Date(now.getFullYear(), now.getMonth() - 1, 1, 0, 0, 0)
      end = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59)
   } else if (scope === 'this_year') {
      start = new Date(now.getFullYear(), 0, 1, 0, 0, 0)
      end = new Date(now.getFullYear(), 11, 31, 23, 59, 59)
   } else if (scope === 'last_year') {
      start = new Date(now.getFullYear() - 1, 0, 1, 0, 0, 0)
      end = new Date(now.getFullYear() - 1, 11, 31, 23, 59, 59)
   } else if (scope === 'custom') {
      if (startDate.value && endDate.value) {
         start = new Date(startDate.value)
         start.setHours(0,0,0,0)
         end = new Date(endDate.value)
         end.setHours(23,59,59,999)
      } else {
         return bookings.value
      }
   }

   if (!start || !end) return bookings.value

   const startTs = start.getTime() / 1000
   const endTs = end.getTime() / 1000

   return bookings.value.filter((b: any) => {
      if (!b.createdAt) return false
      return b.createdAt >= startTs && b.createdAt <= endTs
   })
})

const { allAgentBookings, subAgentBookings, subAgents } = Agent()
const { allCorporateBookings } = Corporate()
const { allBookings } = Admin()
const isLoading = ref<boolean>(true)
const openDialog = ref<boolean>(false)
const selectedBooking = ref<any>(null)
const props = defineProps(['subUserId']) 
let authAdminUser = ref<any>(null)

const { searchLocation } = Location()
const pickupAddresses = ref<any[]>([])
const dropoffAddresses = ref<any[]>([])
const isPickupLoading = ref(false)
const isDropoffLoading = ref(false)
const isPickupDropOpen = ref(false)
const isDropoffDropOpen = ref(false)
let pickupTimeout: ReturnType<typeof setTimeout> | null = null
let dropoffTimeout: ReturnType<typeof setTimeout> | null = null

const handlePickupSearch = () => {
   const val = editForm.value.pickup_location.trim()
   if (val.length > 2) {
      if (pickupTimeout) clearTimeout(pickupTimeout)
      pickupTimeout = setTimeout(async () => {
         isPickupLoading.value = true
         const response = await searchLocation(val)
         isPickupLoading.value = false
         if (response.status === 200) {
            const predictions = sortPlacePredictions(response.data.data)
            pickupAddresses.value = predictions.map((item: any) => ({
               name: item.name,
               isAirport: item.isAirport
            }))
            isPickupDropOpen.value = pickupAddresses.value.length > 0
         }
      }, 400)
   } else {
      isPickupDropOpen.value = false
   }
}

const selectPickupAddress = (item: any) => {
   editForm.value.pickup_location = item.name
   isPickupDropOpen.value = false
}

const handleDropoffSearch = () => {
   const val = editForm.value.dropoff_location.trim()
   if (val.length > 2) {
      if (dropoffTimeout) clearTimeout(dropoffTimeout)
      dropoffTimeout = setTimeout(async () => {
         isDropoffLoading.value = true
         const response = await searchLocation(val)
         isDropoffLoading.value = false
         if (response.status === 200) {
            const predictions = sortPlacePredictions(response.data.data)
            dropoffAddresses.value = predictions.map((item: any) => ({
               name: item.name,
               isAirport: item.isAirport
            }))
            isDropoffDropOpen.value = dropoffAddresses.value.length > 0
         }
      }, 400)
   } else {
      isDropoffDropOpen.value = false
   }
}

const selectDropoffAddress = (item: any) => {
   editForm.value.dropoff_location = item.name
   isDropoffDropOpen.value = false
}

const clickOutsideListener = () => {
   isPickupDropOpen.value = false
   isDropoffDropOpen.value = false
}

onMounted(async () => {
   document.addEventListener('click', clickOutsideListener)

   const authUser = isLoggedIn()
   authAdminUser = isAdminLoggedIn()

   if (route.query.status) {
      statusFilterValue.value = route.query.status as string
      applyStatusFilter()
   }

   await fetchBookings(authUser ? authUser.user.type : '')
})

onBeforeUnmount(() => {
   document.removeEventListener('click', clickOutsideListener)
})

const isWeb = computed(() => {
   const authUser = isLoggedIn()
   return authUser && authUser.user.type == 'web'
})

const fetchBookings = async (userType: string) => {
   isLoading.value = true
   let response: any = { status: 0, data: [] }

   if (userType === 'corporate') {
      response = await allCorporateBookings()
   } else if (userType === 'agent') {
      if (props.subUserId == '') {
         response = await allAgentBookings()
         await fetchSubAgents()
      } else {
         response = await subAgentBookings(props.subUserId)
      }
   } else if (userType === 'web') {
      try {
         const baseUrl = getBackendBaseUrl()
         response = await axios.get(`${baseUrl}api/bookings/protected/my-list`, {
            headers: {
               'Authorization': `Bearer ${getUserToken()}`,
               'Content-Type': 'application/json',
            }
         })
      } catch (err: any) {
         response = err.response || { status: 500 }
      }
   } else if (authAdminUser.value) {
      response = await allBookings()
   }

   isLoading.value = false

   if (response.status == 200) {
      bookings.value = response.data.sort((a: any, b: any) => {
         const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0
         const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0
         return dateB - dateA
      })
   } else if (response.response && response.response.status == 401) {
      logout(router)
   }
}

const fetchSubAgents = async () => {

   const response = await subAgents()

   if (response.status == 200) {
      mySubAgents.value = response.data
   } else {
      const error = handleApiError(response)
      console.log(error)
   }
}

const handleBookingDetails = (booking: any) => {

   selectedBooking.value = booking
   openDialog.value = true
}

const getBookingStatusText = (data: any) => {
   if (!data) return 'Pending and Unpaid'
   if (data.status === 'cancelled') {
      return 'Cancelled'
   }
   if (data.status === 'confirmed') {
      if (data.payment?.status === 'paid') {
         return data.payment?.wallet === 'wallet' ? 'Agency Credit' : 'Paid'
      }
      return 'Confirmed and Unpaid'
   }
   return 'Pending and Unpaid'
}

const getBookingStatusClass = (data: any) => {
   if (!data) return 'bg-purple-50 text-purple-700 border border-purple-200'
   if (data.status === 'cancelled') {
      return 'bg-red-50 text-red-700 border border-red-200'
   }
   if (data.status === 'confirmed') {
      if (data.payment?.status === 'paid') {
         return data.payment?.wallet === 'wallet' 
            ? 'bg-blue-50 text-blue-700 border border-blue-200' 
            : 'bg-green-50 text-green-700 border border-green-200'
      }
      return 'bg-amber-50 text-amber-700 border border-amber-200'
   }
   return 'bg-purple-50 text-purple-700 border border-purple-200'
}

const getStatusClass = (status: string) => {
   if (status == 'incomplete') {
      return 'bg-purple-50 text-purple-700 border border-purple-200'
   } else if (status == 'confirmed') {
      return 'bg-green-50 text-green-700 border border-green-200'
   } else if (status == 'awaiting_payment') {
      return 'bg-amber-50 text-amber-700 border border-amber-200'
   } else if (status == 'need_confirmation') {
      return 'bg-blue-50 text-blue-700 border border-blue-200'
   } else if (status == 'cancelled') {
      return 'bg-red-50 text-red-700 border border-red-200'
   } else if (status == 'unconfirmed') {
      return 'bg-blue-50 text-blue-700 border border-blue-200'
   } else {
      return 'bg-gray-50 text-gray-700 border border-gray-200'
   }
}

const filters = ref()
const statuses = STATUSES.map(status => status.title)
const mySubAgents = ref<any[]>([])
const loading = ref(false)
const dt = ref()

const statusFilterValue = ref('')
const bookedByFilterValue = ref('')
const sourceFilterValue = ref('')

const applyStatusFilter = () => {
   if (statusFilterValue.value) {
      filters.value['status'].constraints[0].value = statusFilterValue.value
   } else {
      filters.value['status'].constraints[0].value = null
   }
}

const applyBookedByFilter = () => {
   if (bookedByFilterValue.value) {
      const agentObj = mySubAgents.value.find((a: any) => a.name === bookedByFilterValue.value)
      filters.value['bookedBy'].value = agentObj ? [agentObj] : null
   } else {
      filters.value['bookedBy'].value = null
   }
}

const applySourceFilter = () => {
   if (sourceFilterValue.value) {
      filters.value['source'].constraints[0].value = sourceFilterValue.value
   } else {
      filters.value['source'].constraints[0].value = null
   }
}

const initFilters = () => {
   filters.value = {
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
      number: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
      status: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }] },
      'search.pickUp.date': { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }] },
      bookedBy: { value: null, matchMode: FilterMatchMode.IN },
      source: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }] },
   }
}

initFilters()

const clearFilter = () => {
   initFilters()
   statusFilterValue.value = ''
   bookedByFilterValue.value = ''
   sourceFilterValue.value = ''
}

const exportCSV = (event: any) => {
   dt.value.exportCSV()
}

const makeBookingPayment = (booking: any) => {

   saveUserSearch(booking.search)
   saveBookingNumber(booking.number)
   saveSelectedCar(booking.car)
   router.push({name: 'BookingWizard', params: {step: 'contact-details'}})
}

const downloadingItem = ref<any>(null)
const downloadInvoiceRef = ref<HTMLElement | null>(null)
const downloadVoucherRef = ref<HTMLElement | null>(null)

const downloadDocument = async (type: 'voucher' | 'invoice', booking: any) => {
   const number = booking.number
   if (!number) return

   try {
      const baseUrl = getBackendBaseUrl()
      // invoice maps to downloadReceiptPdf endpoint
      const endpoint = type === 'invoice' ? 'receipt' : 'voucher'
      const url = `${baseUrl}api/bookings/pdf/${endpoint}/${number}`

      // Fetch the PDF blob from Laravel
      const response = await fetch(url)
      if (!response.ok) throw new Error(`Failed to download ${type} PDF`)

      const blob = await response.blob()
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = type === 'invoice' ? `Invoice_${number}.pdf` : `Voucher_${number}.pdf`
      link.click()
      URL.revokeObjectURL(link.href)
   } catch (err) {
      console.error('Download failed:', err)
   }
}

// Edit & Cancel Request States & Logic
const isEditModalOpen = ref(false)
const isCancelModalOpen = ref(false)
const editStep = ref<'form' | 'preview'>('form')
const cancelReason = ref('')
const cancelReasonError = ref(false)
const editingBooking = ref<any>(null)
const notif = ref<NotificationType>({ show: false, type: 'success', message: '', messageDetails: '' })

const notifClosed = () => {
   notif.value.show = false
}

const editForm = ref({
   contact_name: '',
   contact_email: '',
   contact_phone: '',
   contact_flight_number: '',
   pickup_date: '',
   pickup_time: '',
   hours: 10,
   pickup_location: '',
   dropoff_location: '',
   comments: ''
})

const changeSummary = ref<Array<{ field: string, old: string, new: string, key: string }>>([])

const handleEditBooking = (booking: any) => {
   editingBooking.value = booking

   // Format Date to YYYY-MM-DD for HTML5 date input
   let rawDate = booking.search?.pickUp?.date || '';
   if (rawDate) {
      const parts = rawDate.split(/[-/]/);
      if (parts.length === 3) {
         if (parts[2].length === 4) { // DD-MM-YYYY
            rawDate = `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
         } else if (parts[0].length === 4) { // YYYY-MM-DD
            rawDate = `${parts[0]}-${parts[1].padStart(2, '0')}-${parts[2].padStart(2, '0')}`;
         }
      }
   }

   // Format Time to 24h format (HH:mm) for HTML5 time input
   let rawTime = booking.search?.pickUp?.time || '';
   if (rawTime) {
      const match = rawTime.match(/^(\d{1,2}):(\d{2})\s*(am|pm)?$/i);
      if (match) {
         let hours = parseInt(match[1]);
         const minutes = match[2];
         const ampm = match[3] ? match[3].toLowerCase() : '';
         if (ampm === 'pm' && hours < 12) {
            hours += 12;
         } else if (ampm === 'am' && hours === 12) {
            hours = 0;
         }
         rawTime = `${String(hours).padStart(2, '0')}:${minutes}`;
      }
   }

   editForm.value = {
      contact_name: booking.contact?.name || booking.contact_name || '',
      contact_email: booking.contact?.email || booking.contact_email || '',
      contact_phone: booking.contact?.phone || booking.contact_phone || '',
      contact_flight_number: booking.contact_flight_number || '',
      pickup_date: rawDate,
      pickup_time: rawTime,
      hours: booking.search?.hours || 10,
      pickup_location: booking.search?.pickUp?.location?.name || '',
      dropoff_location: booking.search?.dropOff?.location?.name || '',
      comments: booking.comments || ''
   }
   editStep.value = 'form'
   isEditModalOpen.value = true
}

const generateChangeSummary = () => {
   const summary = []
   const orig = editingBooking.value
   const form = editForm.value

   const origName = orig.contact?.name || orig.contact_name || ''
   if (origName !== form.contact_name) {
      summary.push({ field: 'Contact Name', old: origName, new: form.contact_name, key: 'contact_name' })
   }

   const origEmail = orig.contact?.email || orig.contact_email || ''
   if (origEmail !== form.contact_email) {
      summary.push({ field: 'Contact Email', old: origEmail, new: form.contact_email, key: 'contact_email' })
   }

   const origPhone = orig.contact?.phone || orig.contact_phone || ''
   if (origPhone !== form.contact_phone) {
      summary.push({ field: 'Contact Phone', old: origPhone, new: form.contact_phone, key: 'contact_phone' })
   }

   const origFlight = orig.contact_flight_number || ''
   if (origFlight !== form.contact_flight_number) {
      summary.push({ field: 'Flight Number', old: origFlight, new: form.contact_flight_number, key: 'flight_number' })
   }

   const origDate = orig.search?.pickUp?.date || ''
   if (origDate !== form.pickup_date) {
      summary.push({ field: 'Pickup Date', old: origDate, new: form.pickup_date, key: 'pickup_date' })
   }

   const origTime = orig.search?.pickUp?.time || ''
   if (origTime !== form.pickup_time) {
      summary.push({ field: 'Pickup Time', old: origTime, new: form.pickup_time, key: 'pickup_time' })
   }

   const isHourly = orig.type === 'hourly' || orig.search?.type === 'hourly' || orig.type === 'chauffer'
   if (isHourly) {
      const origHours = orig.search?.hours || 10
      if (parseInt(origHours) !== parseInt(form.hours as any)) {
         summary.push({ field: 'Hours', old: String(origHours), new: String(form.hours), key: 'hours' })
      }
   }

   const origPickupLoc = orig.search?.pickUp?.location?.name || ''
   if (origPickupLoc !== form.pickup_location) {
      summary.push({ field: 'Pickup Location', old: origPickupLoc, new: form.pickup_location, key: 'pickup_location' })
   }

   if (!isHourly) {
      const origDropoffLoc = orig.search?.dropOff?.location?.name || ''
      if (origDropoffLoc !== form.dropoff_location) {
         summary.push({ field: 'Drop-off Location', old: origDropoffLoc, new: form.dropoff_location, key: 'dropoff_location' })
      }
   }

   const origComments = orig.comments || ''
   if (origComments !== form.comments) {
      summary.push({ field: 'Comments', old: origComments, new: form.comments, key: 'comments' })
   }

   changeSummary.value = summary
}

const showChangePreview = () => {
   generateChangeSummary()
   if (changeSummary.value.length === 0) {
      notif.value = { show: true, type: 'error', message: 'No changes', messageDetails: 'No fields were modified.' }
      return
   }
   editStep.value = 'preview'
}

const submitChangeRequest = async () => {
   isLoading.value = true
   try {
      const payloadChanges: Record<string, { old: string, new: string }> = {}
      changeSummary.value.forEach(item => {
         payloadChanges[item.field] = { old: item.old, new: item.new }
      })

      const baseUrl = getBackendBaseUrl()
      const url = isWeb.value
         ? `${baseUrl}api/bookings/protected/${editingBooking.value.id}/change-request`
         : `${baseUrl}api/agent/bookings/${editingBooking.value.id}/change-request`
      const response = await axios.post(url, {
         changes: payloadChanges
      }, {
         headers: {
            'Authorization': `Bearer ${getUserToken()}`,
            'Content-Type': 'application/json'
         }
      })

      if (response.status === 201) {
         isEditModalOpen.value = false
         notif.value = { show: true, type: 'success', message: 'Submitted', messageDetails: 'Your change request has been submitted. Our agency will get back to you shortly.' }
         setTimeout(() => { notifClosed() }, 5000)
      } else {
         notif.value = { show: true, type: 'error', message: 'Error', messageDetails: 'Failed to submit change request. Please try again.' }
      }
   } catch (err: any) {
      console.error(err)
      const errorMsg = err.response?.data?.error || 'An error occurred.'
      notif.value = { show: true, type: 'error', message: 'Submission Failed', messageDetails: errorMsg }
   } finally {
      isLoading.value = false
   }
}

const handleCancelBooking = (booking: any) => {
   editingBooking.value = booking
   cancelReason.value = ''
   cancelReasonError.value = false
   isCancelModalOpen.value = true
}

const submitCancellationRequest = async () => {
   if (!cancelReason.value.trim()) {
      cancelReasonError.value = true
      return
   }

   isLoading.value = true
   try {
      const baseUrl = getBackendBaseUrl()
      const url = isWeb.value
         ? `${baseUrl}api/bookings/protected/${editingBooking.value.id}/cancel-request`
         : `${baseUrl}api/agent/bookings/${editingBooking.value.id}/cancel-request`
      const response = await axios.post(url, {
         reason: cancelReason.value
      }, {
         headers: {
            'Authorization': `Bearer ${getUserToken()}`,
            'Content-Type': 'application/json'
         }
      })

      if (response.status === 201) {
         isCancelModalOpen.value = false
         notif.value = { show: true, type: 'success', message: 'Submitted', messageDetails: 'Your cancel request has been submitted. Our team will get back to you shortly.' }
         setTimeout(() => { notifClosed() }, 5000)
      } else {
         notif.value = { show: true, type: 'error', message: 'Error', messageDetails: 'Failed to submit cancellation request. Please try again.' }
      }
   } catch (err: any) {
      console.error(err)
      const errorMsg = err.response?.data?.error || 'An error occurred.'
      notif.value = { show: true, type: 'error', message: 'Submission Failed', messageDetails: errorMsg }
   } finally {
      isLoading.value = false
   }
}

</script>